import { matchPath, useLocation } from 'react-router-dom'
import { authProtectedFlattenRoutes, publicProtectedFlattenRoutes } from '@/routes'
import type { RoutesProps } from '@/routes'
import { formatDocumentTitle } from '@/utils/documentTitle'

const HOME_ROUTE_NAMES = new Set(['Home', 'Root'])

const PAGE_TITLE_ALIASES: Record<string, string> = {
	Home: '',
	Root: '',
	Dashboards: 'Dashboard',
	'Error - 404': 'Page Not Found',
	'Error - 500': 'Server Error',
	manage_emaillog: 'Email Log',
	manage_emailTemplates: 'Email Templates',
	manage_countryLocalization: 'Language',
	manage_platform_look_ups: 'Platform Lookup Values',
	buy_subscription_plan: 'Choose a Plan',
	view_sub_menus: 'Administrator',
	promo_code_usage_history: 'Promo Code Usage',
	recent_activity: 'Recent Activities',
	logs: 'Logs',
}

const DocumentTitle = () => {
	const { pathname } = useLocation()
	const title = formatDocumentTitle(resolvePageTitle(pathname))

	return <title>{title}</title>
}

function resolvePageTitle(pathname: string): string {
	const match = findBestRouteMatch(pathname, [...publicProtectedFlattenRoutes, ...authProtectedFlattenRoutes])

	if (!match?.name) {
		return pathname === '/' ? '' : 'Page Not Found'
	}

	if (HOME_ROUTE_NAMES.has(match.name)) {
		return ''
	}

	return humanizeRouteName(match.name)
}

function findBestRouteMatch(pathname: string, routes: RoutesProps[]): RoutesProps | undefined {
	let best: { route: RoutesProps; score: number } | undefined

	for (const route of routes) {
		if (!route.path || route.path === '*' || !route.element) {
			continue
		}

		const matched = matchPath({ path: route.path, end: true }, pathname)
		if (!matched) {
			continue
		}

		const score = route.path.replace(/:[^/]+/g, ':').length
		if (!best || score > best.score) {
			best = { route, score }
		}
	}

	return best?.route
}

function humanizeRouteName(name: string): string {
	if (name in PAGE_TITLE_ALIASES) {
		return PAGE_TITLE_ALIASES[name]
	}

	return name
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/\b\w/g, (char) => char.toUpperCase())
}

export default DocumentTitle
